import React, { ReactNode, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Cost } from '@r1-frontend/ui-react/components/costs';
import { TPeriod, TPurchaseOptionsProps } from '@r1-frontend/ui-react/components/costs/PurchaseOptions/types';
import HtmlContentFromApi from '@r1-frontend/ui-react/components/HtmlContentFromApi';
import Characteristics from '@r1-frontend/ui-react/components/List/Characteristics';
import { TCharacteristicsProps } from '@r1-frontend/ui-react/components/List/Characteristics/Characteristics';
import NukaCarousel from '@r1-frontend/ui-react/components/NukaCarousel';
import { H3, H4 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import { FullWidthListContainer, IndentContainer } from '@r1-frontend/ui-react/experimental/containers';

import { EShopCategory } from '~/src/api/content/shop/getShopItems';
import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import { EquipmentControls } from '~/src/features/full-buy/components/EquipmentControls';
import PurchaseOptionsSlider from '~/src/features/full-buy/components/PurchaseOptionsSlider';
import { getIsDisabledByLimits, getLimits, getTextForOptions } from '~/src/features/full-buy/helpersForProducts';
import { TheScrollbar } from '~/src/features/full-buy/popups/BasePopupRightAlign';
import { selectProductsByCategory } from '~/src/features/full-buy/store/selectors/products/selectProductsByCategory';
import { TState } from '~/src/store';

import * as ST from './styled';

type TEquipmentInfoProps = {
    title: string,
    body: string,
    characteristics: TCharacteristicsProps[],
    description: string,
    category: EShopCategory.Routers | EShopCategory.PrivateSectorEquipment | EShopCategory.Decoders,
    quantity: number,
    onIncrement: () => void,
    onDecrement: () => void,
    onClose: () => void,
    images?: string[],
    labels?: ReactNode[],
} & TPurchaseOptionsProps;

const EquipmentInfo = ({
    title,
    body,
    images,
    characteristics,
    description,
    category,
    options,
    labels,
    quantity,
    onDecrement,
    onIncrement,
    onClose,
    onSelect,
}: TEquipmentInfoProps) => {
    const {
        1: fullPriceOption,
    } = options;
    const [selectedOption, setOption] = useState<{ cost: number, period: TPeriod }>(fullPriceOption);
    const analytics = useAnalytics();

    const equipments = useSelector((state: TState) => selectProductsByCategory(state, category));

    const { limits, isDisabled } = useMemo(() => {
        const limits = getLimits(equipments, category);
        const isDisabled = getIsDisabledByLimits({
            limits,
            duration: selectedOption.period,
            productQuantity: quantity,
        });

        return { limits, isDisabled };
    }, [equipments, category, selectedOption, quantity]);


    const handleSelect = (cost: number, period: TPeriod) => () => {
        analytics.equipment.chooseCostType(`type:${category}|name:${title}|`);
        if (period) {
            analytics.equipment.chooseDuration(`type:${category}|name:${title}|`);
        }
        setOption({ cost, period });
    };

    const handleAdd = () => onSelect(selectedOption.cost, selectedOption.period);

    const handleClose = () => {
        analytics.equipment.chooseEquipmentContinue();
        onClose();
    };

    const textForOptions = getTextForOptions(selectedOption.period);

    return (
        <>
            <TheScrollbar>
                {images && (
                    <ST.CarouselContainer>
                        <NukaCarousel
                            slidesToShow={1}
                        >
                            {images
                                .map((item) => <img
                                    key={item}
                                    src={item}
                                    alt={`Изображение продукта ${title}`}
                                />)}
                        </NukaCarousel>
                    </ST.CarouselContainer>
                )}

                <ST.RoundedContainer $desktop={[32, 32, 24, 32]} $mobile={[24]}>
                    <FullWidthListContainer $direction='column' $desktop={8} $mobile={8}>
                        <FullWidthListContainer $direction='row' $size={8}>
                            {labels}
                        </FullWidthListContainer>
                        <H3 $withIndent={false}>{title}</H3>
                        <Paragraph4 $withIndent dangerouslySetInnerHTML={{ __html: body }}/>
                        <H3 $withIndent={false}>
                            {
                                selectedOption.period > 0
                                    ? 'Рассрочка от '
                                    : 'Полная стоимость '
                            }
                            <Cost isMonthly={selectedOption.period > 0}>
                                {selectedOption.cost}
                            </Cost>
                        </H3>
                    </FullWidthListContainer>
                </ST.RoundedContainer>
                <PurchaseOptionsSlider
                    options={options}
                    selectedOption={selectedOption}
                    handleSelect={handleSelect}
                    hasAdded={!!quantity}
                    limits={limits}
                />
                <ST.ContentContainer $desktop={[0, 32]} $mobile={[0, 24]}>
                    <FullWidthListContainer $direction='column' $desktop={24} $mobile={24}>
                        <FullWidthListContainer $direction='column' $desktop={8} $mobile={8}>
                            <H4 $withIndent={false}>Характеристики</H4>
                            {characteristics.map((item, index) => (
                                <Characteristics
                                    key={`${item.title}_${index}`}
                                    title={item.title}
                                    value={item.value}
                                />
                            ))}
                        </FullWidthListContainer>
                        <TextWrapper>
                            <H4 $withIndent={false}>Описание</H4>
                            <HtmlContentFromApi>
                                {description}
                            </HtmlContentFromApi>
                        </TextWrapper>
                    </FullWidthListContainer>

                </ST.ContentContainer>
            </TheScrollbar>

            <ST.Footer>
                <FullWidthListContainer $direction='column' $mobile={12} $desktop={24}>
                    <IndentContainer $mobile={[12]} $desktop={[24]}>
                        <EquipmentControls
                            {...textForOptions}
                            onAddClick={handleAdd}
                            cost={selectedOption.cost}
                            quantity={quantity}
                            onIncrement={onIncrement}
                            onDecrement={onDecrement}
                            onClose={handleClose}
                            disabled={isDisabled}
                        />
                    </IndentContainer>
                </FullWidthListContainer>
            </ST.Footer>
        </>
    );
};

export default EquipmentInfo;
